/**
 * A token's measured box, when it has one — used only as a tie-breaker (see
 * {@link matchTokens}); tokens without rects (e.g. unit tests) match purely
 * by key order.
 */
interface MatchableToken {
  key: string;
  rect?: {x: number; y: number; width: number; height: number};
}

/**
 * Movement cost of pairing `source` with `target`: the Manhattan distance
 * between their rect centers, in canvas px. `0` when either side has no rect,
 * so keyed-only tokens degrade to plain LCS matching.
 */
function movementCost(source: MatchableToken, target: MatchableToken): number {
  if (source.rect === undefined || target.rect === undefined) {
    return 0;
  }
  const dx =
    target.rect.x +
    target.rect.width / 2 -
    (source.rect.x + source.rect.width / 2);
  const dy =
    target.rect.y +
    target.rect.height / 2 -
    (source.rect.y + source.rect.height / 2);
  return Math.abs(dx) + Math.abs(dy);
}

/**
 * Backtracking choice for one DP cell — how the optimum at that cell was
 * reached: `'match'` pairs `source[i-1]` with `target[j-1]` (diagonal step),
 * `'remove'` drops `source[i-1]` (step up), `'add'` inserts `target[j-1]`
 * (step left), and `'none'` marks an undecided boundary cell (`i === 0` or
 * `j === 0`). See {@link matchTokens}.
 */
type MatchChoice = 'none' | 'match' | 'remove' | 'add';

/**
 * Matches two ordered lists of keyed tokens via longest common subsequence
 * (LCS) — the same class of algorithm text-diff tools use. Tokens outside
 * the LCS are `removed` (present only in `source`) or `added` (present
 * only in `target`); tokens within it are paired in `matched`, in order.
 *
 * This mirrors how manim's `TransformMatchingTex` pairs glyphs between two
 * TeX expressions: identical, in-order runs are treated as "the same
 * thing, just moved," while anything that doesn't line up is treated as a
 * genuine removal/addition — not a shape warp. Matching by content alone
 * (ignoring position) would over-match repeated characters; LCS instead
 * prefers the longest *in-order* run of matches, which is what keeps
 * e.g. two `x`s in different terms from swapping places.
 *
 * Many inputs admit several equally-long LCSes that pair *different*
 * occurrences of a repeated key — e.g. `a^2+b^2 → a^2+b^2=c^2` can pair
 * b's `2` with either b's or c's `2` and match five tokens either way.
 * Ties are broken by minimizing the total movement of matched pairs
 * (rect-center distance, see {@link movementCost}), so each glyph pairs
 * with its spatially closest valid occurrence and unchanged glyphs hold
 * still instead of sliding onto newly-added lookalikes.
 *
 * @param source - The tokens present before the transition, in reading order.
 * @param target - The tokens present after the transition, in reading order.
 * @return `matched` pairs (source token, target token) in order, plus the
 *     `removed` (source-only) and `added` (target-only) tokens, each in
 *     their own original order.
 * @throws Never.
 */
export function matchTokens<T extends MatchableToken>(
  source: T[],
  target: T[],
): {matched: Array<[T, T]>; removed: T[]; added: T[]} {
  const sourceLength = source.length;
  const targetLength = target.length;

  // Dynamic-programming tables over source[0..i) × target[0..j):
  // `lcsLength[i][j]` is the standard LCS length; `moveCost[i][j]` is the
  // smallest total movement (see `movementCost`) any matching achieving that
  // length can have; `choice[i][j]` records the winning step for backtracking
  // (recomputing the comparison during backtracking would have to replicate
  // the exact tie-break order — recording it is simpler and can't drift).
  const lcsLength: number[][] = Array.from({length: sourceLength + 1}, () =>
    new Array<number>(targetLength + 1).fill(0),
  );
  const moveCost: number[][] = Array.from({length: sourceLength + 1}, () =>
    new Array<number>(targetLength + 1).fill(0),
  );
  const choice: MatchChoice[][] = Array.from({length: sourceLength + 1}, () =>
    new Array<MatchChoice>(targetLength + 1).fill('none'),
  );
  for (let i = 1; i <= sourceLength; i++) {
    for (let j = 1; j <= targetLength; j++) {
      // Start from the better of the two skip options (prefer removing from
      // source on a pure tie, matching the classic backtrack convention).
      let bestLength = lcsLength[i - 1][j];
      let bestCost = moveCost[i - 1][j];
      let bestChoice: MatchChoice = 'remove';
      if (
        lcsLength[i][j - 1] > bestLength ||
        (lcsLength[i][j - 1] === bestLength && moveCost[i][j - 1] < bestCost)
      ) {
        bestLength = lcsLength[i][j - 1];
        bestCost = moveCost[i][j - 1];
        bestChoice = 'add';
      }
      if (source[i - 1].key === target[j - 1].key) {
        const matchLength = lcsLength[i - 1][j - 1] + 1;
        const matchCost =
          moveCost[i - 1][j - 1] + movementCost(source[i - 1], target[j - 1]);
        // Prefer the match on equal length + cost: more pairs at the same
        // altitude never hurts, and it keeps keyed-only inputs (all costs 0)
        // matching exactly like plain LCS.
        if (
          matchLength > bestLength ||
          (matchLength === bestLength && matchCost <= bestCost)
        ) {
          bestLength = matchLength;
          bestCost = matchCost;
          bestChoice = 'match';
        }
      }
      lcsLength[i][j] = bestLength;
      moveCost[i][j] = bestCost;
      choice[i][j] = bestChoice;
    }
  }

  // Backtrack the recorded choices from the bottom-right corner.
  const matched: Array<[T, T]> = [];
  const removed: T[] = [];
  const added: T[] = [];
  let i = sourceLength;
  let j = targetLength;
  while (i > 0 && j > 0) {
    switch (choice[i][j]) {
      case 'match':
        matched.push([source[i - 1], target[j - 1]]);
        i--;
        j--;
        break;
      case 'remove':
        removed.push(source[i - 1]);
        i--;
        break;
      default:
        added.push(target[j - 1]);
        j--;
        break;
    }
  }
  while (i > 0) {
    removed.push(source[i - 1]);
    i--;
  }
  while (j > 0) {
    added.push(target[j - 1]);
    j--;
  }

  matched.reverse();
  removed.reverse();
  added.reverse();
  return {matched, removed, added};
}
